/**
 * 2ECOMP MODIFIED FILE - Contains customizations
 *
 * Customizations:
 * - PrintHeader: Custom header for PDF/print export
 * - printStyles.css: Print-specific styling
 * - nativePostMessage: PDF generation support
 * - getExportValue: Custom Excel export formatting
 * - formControlSx/selectSx: Custom form styling
 *
 * DO NOT overwrite with upstream without merging customizations.
 * Search for "2ECOMP" comments to find all customization points.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FormControl, InputLabel, Select, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Link, IconButton,
} from '@mui/material';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { formatSpeed, formatTime } from '../common/util/formatter';
import ReportFilter, { updateReportParams } from './components/ReportFilter';
import { prefixString, unprefixString } from '../common/util/stringUtils';
import { useTranslation, useTranslationKeys } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import usePersistedState from '../common/util/usePersistedState';
import ColumnSelect from './components/ColumnSelect';
import { useCatch, useEffectAsync } from '../reactHelper';
import useReportStyles from './common/useReportStyles';
import TableShimmer from '../common/components/TableShimmer';
import { useAttributePreference } from '../common/util/preferences';
import MapView from '../map/core/MapView';
import MapGeofence from '../map/MapGeofence';
import MapPositions from '../map/MapPositions';
import MapCamera from '../map/MapCamera';
import scheduleReport from './common/scheduleReport';
import MapScale from '../map/MapScale';
import SelectField from '../common/components/SelectField';
import fetchOrThrow from '../common/util/fetchOrThrow';
import AddressValue from '../common/components/AddressValue';
import exportExcel from '../common/util/exportExcel';
// 2ECOMP START: Print/PDF support
import { nativePostMessage } from '../common/components/NativeInterface';
import { sessionActions } from '../store';
import PrintHeader from './common/PrintHeader';
import './common/printStyles.css';
// 2ECOMP END

// 2ECOMP START: Custom form styling
const formControlSx = {
  minWidth: 240,
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    '& fieldset': { borderColor: 'divider' },
    '&:hover fieldset': { borderColor: 'primary.main' },
    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
  },
};

const selectSx = { backgroundColor: 'background.paper' };
// 2ECOMP END

const columnsArray = [
  ['eventTime', 'positionFixTime'],
  ['type', 'sharedType'],
  ['geofenceId', 'sharedGeofence'],
  ['maintenanceId', 'sharedMaintenance'],
  ['address', 'positionAddress'],
  ['attributes', 'commandData'],
];
const columnsMap = new Map(columnsArray);

const EventReportPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useReportStyles();
  const t = useTranslation();
  const theme = useTheme();

  const [searchParams, setSearchParams] = useSearchParams();

  const devices = useSelector((state) => state.devices.items);
  const geofences = useSelector((state) => state.geofences.items);
  const printing = useSelector((state) => state.session.printing ?? false);

  const speedUnit = useAttributePreference('speedUnit');

  const [allEventTypes, setAllEventTypes] = useState([['allEvents', 'eventAll']]);

  const alarms = useTranslationKeys((it) => it.startsWith('alarm')).map((it) => ({
    key: unprefixString('alarm', it),
    name: t(it),
  }));

  const [columns, setColumns] = usePersistedState('eventColumns', ['eventTime', 'type', 'address', 'attributes']);
  const eventTypes = useMemo(() => searchParams.getAll('eventType'), [searchParams]);
  const alarmTypes = useMemo(() => searchParams.getAll('alarmType'), [searchParams]);
  const [items, setItems] = useState([]);
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [position, setPosition] = useState(null);
  // 2ECOMP START: Print/PDF state management
  const [hasResults, setHasResults] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  // 2ECOMP END

  useEffect(() => {
    if (!eventTypes.length) {
      updateReportParams(searchParams, setSearchParams, 'eventType', ['allEvents']);
    }
  }, [searchParams, setSearchParams, eventTypes])

  useEffect(() => {
    if (selectedItem?.positionId) {
      setPosition(positions[selectedItem.positionId] || null);
    } else {
      setPosition(null);
    }
  }, [selectedItem, positions]);

useEffectAsync(async () => {
  const response = await fetchOrThrow('/api/notifications/types');
  const types = await response.json();
  setAllEventTypes([...allEventTypes, ...types.map((it) => [it.type, prefixString('event', it.type)])]);
}, []);

// 2ECOMP START: Print/PDF event handlers
useEffect(() => {
  const handleAfterPrint = () => {
    dispatch(sessionActions.setPrinting(false));
  };
  window.addEventListener('afterprint', handleAfterPrint);
  return () => {
    window.removeEventListener('afterprint', handleAfterPrint);
  };
}, [dispatch]);

useEffect(() => {
  if (printing) {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => nativePostMessage(`print|${t('reportEvents')}.pdf`)),
    );
  }
}, [printing, t]);
// 2ECOMP END

  const loadEvents = async ({ deviceIds = [], groupIds = [], from, to }) => {
    const query = new URLSearchParams({ from, to });
    deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
    groupIds.forEach((groupId) => query.append('groupId', groupId));
    eventTypes.forEach((it) => query.append('type', it));
    if (eventTypes[0] !== 'allEvents' && eventTypes.includes('alarm')) {
      alarmTypes.forEach((it) => query.append('alarm', it));
    }
    setSelectedItem(null);
    setPosition(null);
    setLoading(true);
    try {
      const response = await fetchOrThrow(`/api/reports/events?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      const events = await response.json();
      setItems(events);
      setHasResults(events.length > 0);
      const positionIds = Array.from(new Set(events
        .map((event) => event.positionId)
        .filter((id) => id)));
      const positionsMap = {};
      if (positionIds.length > 0) {
        const positionsQuery = new URLSearchParams();
        positionIds.slice(0, 128).forEach((id) => positionsQuery.append('id', id));
        const positionsResponse = await fetchOrThrow(`/api/positions?${positionsQuery.toString()}`);
        const positionsArray = await positionsResponse.json();
        positionsArray.forEach((p) => positionsMap[p.id] = p);
      }
      setPositions(positionsMap);
    } finally {
      setLoading(false);
    }
  };

  const onShow = useCatch(async ({ deviceIds, groupIds, from, to }) => {
    setSelectedDeviceIds(deviceIds);
    setSelectedGroupIds(groupIds);
    setFromDate(from || '');
    setToDate(to || '');
    await loadEvents({ deviceIds, groupIds, from, to });
  });

  // 2ECOMP START: Custom Excel export formatting
  const getExportValue = (item, key) => {
    const positionRef = positions[item.positionId];
    switch (key) {
      case 'deviceId':
        return devices[item.deviceId]?.name || `${item.deviceId}`;
      case 'eventTime':
        return formatTime(item.eventTime, 'seconds');
      case 'type':
        return t(prefixString('event', item.type));
      case 'geofenceId':
        if (item.geofenceId > 0) {
          const geofence = geofences[item.geofenceId];
          return geofence?.name || `${item.geofenceId}`;
        }
        return '';
      case 'maintenanceId':
        return item.maintenanceId > 0 ? `${item.maintenanceId}` : '';
      case 'address':
        return positionRef?.address || '';
      case 'attributes':
        switch (item.type) {
          case 'alarm':
            return item.attributes?.alarm ? t(prefixString('alarm', item.attributes.alarm)) : '';
          case 'deviceOverspeed':
            return item.attributes?.speed !== undefined
              ? formatSpeed(item.attributes.speed, speedUnit, t)
              : '';
          case 'driverChanged':
            return item.attributes?.driverUniqueId || '';
          case 'media':
            return item.attributes?.file || '';
          case 'commandResult':
            return item.attributes?.result || '';
          default:
            return '';
        }
      default:
        return item[key] ?? '';
    }
  };
  // 2ECOMP END

  const onExport = useCatch(async () => {
    if (!items.length) {
      return;
    }

    const sheets = new Map();
    items.forEach((item) => {
      const deviceName = devices[item.deviceId]?.name || `${item.deviceId}`;
      if (!sheets.has(deviceName)) {
        sheets.set(deviceName, []);
      }
      const row = {};
      columns.forEach((key) => {
        const header = t(columnsMap.get(key));
        row[header] = getExportValue(item, key);
      });
      sheets.get(deviceName).push(row);
    });

    await exportExcel(t('reportEvents'), `${t('reportEvents')}.xlsx`, sheets, theme);
  });

  const onSchedule = useCatch(async (deviceIds, groupIds, report) => {
    report.type = 'events';
    if (eventTypes[0] !== 'allEvents') {
      report.attributes.types = eventTypes.join(',');
    }
    await scheduleReport(deviceIds, groupIds, report);
    navigate('/reports/scheduled');
  });

  // 2ECOMP START: Print handler with data preloading
  const onPrint = useCatch(async ({ deviceIds = [], groupIds = [], from, to } = {}) => {
    const effectiveDeviceIds = deviceIds.length ? deviceIds : selectedDeviceIds;
    const effectiveGroupIds = groupIds.length ? groupIds : selectedGroupIds;
    const effectiveFrom = from ?? fromDate;
    const effectiveTo = to ?? toDate;

    if (!effectiveDeviceIds.length || !effectiveFrom || !effectiveTo) {
      return;
    }

    if (!items.length) {
      await loadEvents({
        deviceIds: effectiveDeviceIds,
        groupIds: effectiveGroupIds,
        from: effectiveFrom,
        to: effectiveTo,
      });
    }

    setSelectedDeviceIds(effectiveDeviceIds);
    setSelectedGroupIds(effectiveGroupIds);
    setFromDate(effectiveFrom);
    setToDate(effectiveTo);
    dispatch(sessionActions.setPrinting(true));
  });
  // 2ECOMP END

  const formatValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value].name;
      case 'eventTime':
        return formatTime(value, 'seconds');
      case 'type':
        return t(prefixString('event', value));
      case 'geofenceId':
        if (value > 0) {
          const geofence = geofences[value];
          return geofence && geofence.name;
        }
        return null;
      case 'maintenanceId':
        return value > 0 ? value : null;
      case 'address': {
        const position = positions[item.positionId];
        if (position) {
          return (
            <AddressValue
              latitude={position.latitude}
              longitude={position.longitude}
              originalAddress={position.address}
            />
          );
        }
        return '';
      }
      case 'attributes':
        switch (item.type) {
          case 'alarm':
            return t(prefixString('alarm', item.attributes.alarm));
          case 'deviceOverspeed':
            return formatSpeed(item.attributes.speed, speedUnit, t);
          case 'driverChanged':
            return item.attributes.driverUniqueId;
          case 'media':
            return (<Link href={`/api/media/${devices[item.deviceId]?.uniqueId}/${item.attributes.file}`} target="_blank">{item.attributes.file}</Link>);
          case 'commandResult':
            return item.attributes.result;
          default:
            return '';
        }
      default:
        return value;
    }
  };

  // 2ECOMP START: Printable value formatter (returns plain text for PDF)
  const getPrintableValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value]?.name || `${value}`;
      case 'eventTime':
        return formatTime(value, 'seconds');
      case 'type':
        return t(prefixString('event', value));
      case 'geofenceId': {
        if (value > 0) {
          const geofence = geofences[value];
          return geofence?.name || `${value}`;
        }
        return '';
      }
      case 'maintenanceId':
        return value > 0 ? `${value}` : '';
      case 'address': {
        const source = positions[item.positionId];
        return source?.address || '';
      }
      case 'attributes':
        switch (item.type) {
          case 'alarm':
            return t(prefixString('alarm', item.attributes.alarm));
          case 'deviceOverspeed':
            return formatSpeed(item.attributes.speed, speedUnit, t);
          case 'driverChanged':
            return item.attributes.driverUniqueId || '';
          case 'media':
            return item.attributes.file || '';
          case 'commandResult':
            return item.attributes.result || '';
          default:
            return '';
        }
      default:
        return value != null ? `${value}` : '';
    }
  };
  // 2ECOMP END

  // 2ECOMP START: Print/PDF render mode
  const deviceNames = selectedDeviceIds
    .map((deviceId) => devices[deviceId]?.name || devices[deviceId]?.uniqueId || `${deviceId}`)
    .filter(Boolean)
    .join(', ');

  if (printing) {
    return (
      <div>
        <PrintHeader
          title={t('reportEvents')}
          from={fromDate}
          to={toDate}
          device={deviceNames}
        />
        <div>
          <Table id="event-report-table">
            <TableHead>
              <TableRow>
                <TableCell>{t('sharedDevice')}</TableCell>
                {columns.map((key) => (
                  <TableCell key={key}>{t(columnsMap.get(key))}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={`print-${item.id}`}>
                  <TableCell>{devices[item.deviceId]?.name || item.deviceId}</TableCell>
                  {columns.map((key) => (
                    <TableCell key={key}>{getPrintableValue(item, key)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }
  // 2ECOMP END

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportEvents']}>
      <div className={classes.container}>
        {selectedItem && (
          <div className={classes.containerMap}>
            <MapView>
              <MapGeofence />
              {position && <MapPositions positions={[position]} titleField="fixTime" />}
            </MapView>
            <MapScale />
            {position && <MapCamera latitude={position.latitude} longitude={position.longitude} />}
          </div>
        )}
        <div className={classes.containerMain}>
          <div className={classes.header}>
            <ReportFilter
              onShow={onShow}
              onExport={onExport}
              onPrint={onPrint}
              onSchedule={onSchedule}
              deviceType="multiple"
              loading={loading}
              hasResults={hasResults}
            >
              <div className={classes.filterItem}>
                <FormControl fullWidth size="small" variant="outlined" sx={formControlSx}>
                  <InputLabel>{t('reportEventTypes')}</InputLabel>
                  <Select
                    label={t('reportEventTypes')}
                    value={eventTypes}
                    onChange={(e, child) => {
                      let values = e.target.value;
                      const clicked = child.props.value;
                      if (values.includes('allEvents') && values.length > 1) {
                        values = [clicked];
                      }
                      updateReportParams(searchParams, setSearchParams, 'eventType', values)
                    }}
                    multiple
                    size="small"
                    sx={selectSx}
                  >
                    {allEventTypes.map(([key, string]) => (
                      <MenuItem key={key} value={key}>{t(string)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              {eventTypes[0] !== 'allEvents' && eventTypes.includes('alarm') && (
                <div className={classes.filterItem}>
                  <SelectField
                    multiple
                    value={alarmTypes}
                    onChange={(e) => updateReportParams(searchParams, setSearchParams, 'alarmType', e.target.value)}
                    data={alarms}
                    keyGetter={(it) => it.key}
                    label={t('sharedAlarms')}
                    fullWidth
                  />
                </div>
              )}
              <ColumnSelect columns={columns} setColumns={setColumns} columnsArray={columnsArray} />
            </ReportFilter>
          </div>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.columnAction} />
                <TableCell>{t('sharedDevice')}</TableCell>
                {columns.map((key) => (<TableCell key={key}>{t(columnsMap.get(key))}</TableCell>))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading ? items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className={classes.columnAction} padding="none">
                    {(item.positionId && (selectedItem === item ? (
                      <IconButton size="small" onClick={() => setSelectedItem(null)}>
                        <GpsFixedIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton size="small" onClick={() => setSelectedItem(item)}>
                        <LocationSearchingIcon fontSize="small" />
                      </IconButton>
                    ))) || ''}
                  </TableCell>
                  <TableCell>{devices[item.deviceId].name}</TableCell>
                  {columns.map((key) => (
                    <TableCell key={key}>
                      {formatValue(item, key)}
                    </TableCell>
                  ))}
                </TableRow>
              )) : (<TableShimmer columns={columns.length + 2} />)}
            </TableBody>
          </Table>
        </div>
      </div>
    </PageLayout>
  );
};

export default EventReportPage;
